import { PROJECT_FORM_TYPES, AUX_MUTATION } from "../projectFormStore";
import { PROJECT_TYPES } from "SharedStores/Project/projectStore";
import { idGenerator } from "Util/uniqueIdGenerator";
import { COMPONENT_TYPE } from "Types/componentTypes";
import { getValueOrDefault, getForMultiValue, sendMultiDataChange, mapMultiValues } from "./projectElement.js";

const ParentLabel = "Subsystem";
const ChildLabel = "Battery";
const ConfigurationField = "Configuration";
const CalculationFields = ["Total Nameplate Energy Capacity", "Total Maximum Continuous Discharge Rate"];

const inputFields = {
    SubsystemType: ParentLabel + " Type",
    SubsystemQuantity: ParentLabel + " Quantity",
    SubsystemManufacturer: ParentLabel + " Manufacturer",
    SubsystemModel: ParentLabel + " Model",
    SubsystemEnergyRating: ParentLabel + " Energy Rating",
    SubsystemPowerRating: ParentLabel + " Power Rating",
    SubsystemTechnology: ParentLabel + " Technology",
    ChildAssignedParent: ChildLabel + " Assigned " + ParentLabel,
    ChildQuantity: ChildLabel + " Quantity",
    ChildManufacturer: ChildLabel + " Manufacturer",
    ChildModel: ChildLabel + " Model",
    ChildEnergyRating: ChildLabel + " Energy Rating",
    ChildTechnology: ChildLabel + " Technology"
};
const outputFields = {
    Configuration: ConfigurationField,
    EnergyCalculation: CalculationFields[0],
    PowerCalculation: CalculationFields[1]
};

export const SUFFIXES = {
    inputFields,
    outputFields
};

export const ENERGY_STORAGE_SUBSYSTEM_TYPE = {
    Undefined: 0,
    IntegratedEnergyStorageSystem: 1,
    InverterAndBattery: 2
};

// These should match the report values in EnergyStorageConstant.cs
export const ENERGY_STORAGE_SUBSYSTEM_NAME = {
    IntegratedEnergyStorageSystem: "Integrated Energy Storage",
    InverterAndBattery: "Inverter and Battery"
};

const energyStorageSystemIdGenerator = new idGenerator();

export function getParentEquipmentTypeFromSubsystemType(energyStorageSubsystemType) {
    if (energyStorageSubsystemType === ENERGY_STORAGE_SUBSYSTEM_TYPE.IntegratedEnergyStorageSystem) {
        return COMPONENT_TYPE.Integrated_Energy_Storage;
    }
    if (energyStorageSubsystemType === ENERGY_STORAGE_SUBSYSTEM_TYPE.InverterAndBattery) {
        return COMPONENT_TYPE.Storage_System_Inverter;
    }
    return null;
}

export function getChildEquipmentTypeFromSubsystemType(energyStorageSubsystemType) {
    if (energyStorageSubsystemType === ENERGY_STORAGE_SUBSYSTEM_TYPE.InverterAndBattery) {
        return COMPONENT_TYPE.Battery;
    }
    return null;
}

function getParentMultiValue(subsystems, propertyName) {
    return subsystems.map((s) => getForMultiValue(s[propertyName]));
}

function getChildMultiValue(subsystems, propertyName) {
    let childMultiValue = [];
    if (subsystems && subsystems.length > 0) {
        childMultiValue = subsystems
            .map((s) => s.childGroups?.map((b) => getForMultiValue(b[propertyName])))
            .reduce((p, c) => p.concat(c));
    }

    return childMultiValue;
}

function getChildAssignedParentMultiValue(subsystems) {
    let childMultiValue = [];
    if (subsystems && subsystems.length > 0) {
        childMultiValue = subsystems.map((s, index) => s.childGroups?.map(() => index)).reduce((p, c) => p.concat(c));
    }

    return childMultiValue;
}

function computeSubsystems(element, getters) {
    const projectElement = getters[PROJECT_FORM_TYPES.getters.projectElementFromId](element.PublicId);
    const parentIds = projectElement.auxState.parentIds;
    const childItemIds = projectElement.auxState.childItemIds;
    const inputMap = mapMultiValues(element, inputFields, getters[PROJECT_TYPES.getters.dataFieldMultiValueFromId]);

    const subsystems = inputMap.SubsystemQuantity.map((quantity, index) => {
        const energyStorageSubsystemType = Number(getValueOrDefault(inputMap.SubsystemType[index]));
        const parentEquipmentType = getParentEquipmentTypeFromSubsystemType(energyStorageSubsystemType);
        // Must verify these fields exist to cover the interval before the fields are added
        const energyRating = inputMap.SubsystemEnergyRating
            ? getValueOrDefault(inputMap.SubsystemEnergyRating[index])
            : "";
        const powerRating = inputMap.SubsystemPowerRating
            ? getValueOrDefault(inputMap.SubsystemPowerRating[index])
            : "";
        return {
            index,
            id: parentIds[index],
            subsystemType: energyStorageSubsystemType,
            equipmentType: parentEquipmentType,
            quantity: getValueOrDefault(quantity),
            manufacturerId: getValueOrDefault(inputMap.SubsystemManufacturer[index]),
            modelId: getValueOrDefault(inputMap.SubsystemModel[index]),
            energyRating: energyRating,
            powerRating: powerRating,
            equipmentTechnology: getValueOrDefault(inputMap.SubsystemTechnology[index]),
            childGroups: []
        };
    });

    if (subsystems.length > 0 && inputMap.ChildAssignedParent) {
        inputMap.ChildAssignedParent.forEach((parent, index) => {
            const parentNum = parseInt(parent);
            if (Number.isFinite(parentNum) && 0 <= parentNum && parentNum < subsystems.length) {
                // Must verify this field exists to cover the interval before the field is added
                const energyRating = inputMap.ChildEnergyRating
                    ? getValueOrDefault(inputMap.ChildEnergyRating[index])
                    : "";
                const childItem = {
                    index,
                    id: childItemIds[index],
                    assignedParent: parentNum,
                    quantity: getValueOrDefault(inputMap.ChildQuantity[index]),
                    manufacturerId: getValueOrDefault(inputMap.ChildManufacturer[index]),
                    modelId: getValueOrDefault(inputMap.ChildModel[index]),
                    energyRating: energyRating,
                    equipmentTechnology: getValueOrDefault(inputMap.ChildTechnology[index])
                };
                const subsystemType = subsystems[childItem.assignedParent].subsystemType;
                childItem.equipmentType = getChildEquipmentTypeFromSubsystemType(subsystemType);
                subsystems[childItem.assignedParent].childGroups.push(childItem);
            }
        }, this);
    }

    return subsystems;
}

function commitEnergyStorageConfig(element, subsystems, commit, dispatch) {
    const parentIds = getParentMultiValue(subsystems, "id");
    const parentSubsystemTypes = getParentMultiValue(subsystems, "subsystemType");
    const parentQuantities = getParentMultiValue(subsystems, "quantity");
    const parentManufacturerIds = getParentMultiValue(subsystems, "manufacturerId");
    const parentModelIds = getParentMultiValue(subsystems, "modelId");
    const parentEnergyRatings = getParentMultiValue(subsystems, "equipmentEnergyRating");
    const parentPowerRatings = getParentMultiValue(subsystems, "equipmentPowerRating");
    const parentTechnologies = getParentMultiValue(subsystems, "equipmentTechnology");

    const childItemIds = getChildMultiValue(subsystems, "id");
    const childAssignedParents = getChildAssignedParentMultiValue(subsystems);
    const childQuantities = getChildMultiValue(subsystems, "quantity");
    const childManufacturerIds = getChildMultiValue(subsystems, "manufacturerId");
    const childModelIds = getChildMultiValue(subsystems, "modelId");
    const childEnergyRatings = getChildMultiValue(subsystems, "equipmentEnergyRating");
    const childTechnologies = getChildMultiValue(subsystems, "equipmentTechnology");

    commit(
        AUX_MUTATION,
        {
            elementId: element.PublicId,
            auxState: {
                parentIds,
                childItemIds,
                isDirty: true
            }
        },
        { root: true }
    );

    sendMultiDataChange(element, inputFields.SubsystemType, parentSubsystemTypes, dispatch);
    sendMultiDataChange(element, inputFields.SubsystemQuantity, parentQuantities, dispatch);
    sendMultiDataChange(element, inputFields.SubsystemManufacturer, parentManufacturerIds, dispatch);
    sendMultiDataChange(element, inputFields.SubsystemModel, parentModelIds, dispatch);
    sendMultiDataChange(element, inputFields.SubsystemEnergyRating, parentEnergyRatings, dispatch);
    sendMultiDataChange(element, inputFields.SubsystemPowerRating, parentPowerRatings, dispatch);
    sendMultiDataChange(element, inputFields.SubsystemTechnology, parentTechnologies, dispatch);

    sendMultiDataChange(element, inputFields.ChildAssignedParent, childAssignedParents, dispatch);
    sendMultiDataChange(element, inputFields.ChildQuantity, childQuantities, dispatch);
    sendMultiDataChange(element, inputFields.ChildManufacturer, childManufacturerIds, dispatch);
    sendMultiDataChange(element, inputFields.ChildModel, childModelIds, dispatch);
    sendMultiDataChange(element, inputFields.ChildEnergyRatings, childEnergyRatings, dispatch);
    sendMultiDataChange(element, inputFields.ChildTechnology, childTechnologies, dispatch);

    clearOutputValues(element, dispatch);
}

function clearOutputValues(element, dispatch) {
    const promises = [];
    for (const key in outputFields) {
        if (outputFields.hasOwnProperty(key)) {
            const suffix = outputFields[key];
            const dataFieldId = element.GroupDataFields[suffix];
            if (suffix && dataFieldId) {
                promises.push(
                    dispatch(
                        PROJECT_FORM_TYPES.actions.setProjectData,
                        {
                            dataFieldId,
                            value: null
                        },
                        { root: true }
                    )
                );
            }
        }
    }
}

function getEmptyChild(equipmentType) {
    return {
        id: energyStorageSystemIdGenerator.getNextId("energyStorageSubsystemChild"),
        assignedParent: 0,
        equipmentType: equipmentType,
        energyRating: "",
        equipmentTechnology: "",
        quantity: "",
        manufacturerId: "",
        modelId: ""
    };
}

function getEmptySubsystem(energyStorageSubsystemType) {
    const parentEquipmentType = getParentEquipmentTypeFromSubsystemType(energyStorageSubsystemType);
    const childEquipmentType = getChildEquipmentTypeFromSubsystemType(energyStorageSubsystemType);
    const child = childEquipmentType ? [getEmptyChild(childEquipmentType)] : [];
    return {
        id: energyStorageSystemIdGenerator.getNextId("energyStorageSubsystem"),
        subsystemType: energyStorageSubsystemType,
        equipmentType: parentEquipmentType,
        energyRating: "",
        powerRating: "",
        equipmentTechnology: "",
        quantity: "",
        manufacturerId: "",
        modelId: "",
        childGroups: child
    };
}

function getAuxProjectElementState(element, projectElement, Project) {
    const dataFieldMultiValueFromId = (dataFieldId) => Project.DataFields[dataFieldId].MultiInstanceData;
    const inputMap = mapMultiValues(element, inputFields, dataFieldMultiValueFromId);
    const parentIds = inputMap.SubsystemQuantity.map(() =>
        energyStorageSystemIdGenerator.getNextId("energyStorageSubsystem")
    );
    const childItemIds = inputMap.ChildQuantity.map(() =>
        energyStorageSystemIdGenerator.getNextId("energyStorageSubsystemChild")
    );

    return { parentIds, childItemIds };
}

function isComplete(element, getters) {
    // If the form element is not required, it can be left incomplete or even empty
    if (!element.Properties.required) {
        return true;
    }
    // Look for calculated value greater than 0
    return CalculationFields.some((calcField) => {
        const calcFieldId = element.GroupDataFields[calcField];
        const value = getters[PROJECT_TYPES.getters.dataFieldValueFromId](calcFieldId);
        return value && value > 0;
    });
}

export const energyStorageSystemController = {
    computeSubsystems,
    commitEnergyStorageConfig,
    getEmptyChild,
    getEmptySubsystem,
    getAuxProjectElementState,
    isComplete
};
